import { memo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';
import { useBrandConfig } from '../../../../context/BrandConfigContext';
import { RIGHTS, full, useRights, visible } from '../../../../rights';
import ChannelPicker from './definition/ChannelPicker';
import ScheduleFields from './definition/ScheduleFields';
import SidebarSwitch from './definition/SidebarSwitch';
import DeferredTextField from './definition/DeferredTextField';
import { definitionErrors } from './validation/fieldErrors';

// Même limite que BeOne (libellé de l'opération : 50) et que la colonne DEF_TITLE
export const TITLE_MAX_LENGTH = 50;

export type ChannelType = 'email' | 'sms' | 'cashdesk' | 'wallet' | 'courrier' | '';

export interface DefinitionFormData {
  channel: ChannelType;
  title: string;
  description: string;
  sendDate: string;
  sendByCustomer: boolean;
  hasOffer: boolean;
  followUpDateStart: string;
  followUpDateEnd: string;
  isTestOperation: boolean;
  isRecurring: boolean;
  isPreEdited: boolean;
  // Coupon caisse sans création : pas d'étape Création ni de BAT
  noCreation: boolean;
  editionDateStart: string;
  editionDateEnd: string;
}

interface CampaignDefinitionStepProps {
  data: DefinitionFormData;
  onChange: (patch: Partial<DefinitionFormData>) => void;
}

function CampaignDefinitionStep({ data, onChange }: CampaignDefinitionStepProps) {
  const { t } = useLanguage();
  const isCashdesk = data.channel === 'cashdesk';
  const errors = definitionErrors(data);
  const { can, state } = useRights();
  const { setting } = useBrandConfig();
  // Option activée par enseigne (module.test.operation.active) ET soumise au droit da.operation.create_test
  const showTestOperation = isCashdesk && setting('module.test.operation.active') && can(full(RIGHTS.operation.createTest));
  // Droit intermédiaire : switch grisé ; une campagne déjà récurrente garde son switch visible
  const recurringState = state(RIGHTS.planning.defineRecurring);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {t('pages.marketing.communication.definition')}
      </Typography>

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {t('pages.marketing.communication.canal')}
      </Typography>
      <ChannelPicker value={data.channel} onChange={channel => onChange({ channel })} />

      <Divider sx={{ my: 3 }} />

      <DeferredTextField
        required
        label={t('pages.marketing.communication.title')}
        value={data.title}
        onCommit={title => onChange({ title })}
        maxLength={TITLE_MAX_LENGTH}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
        {t('pages.marketing.communication.programmation')}
      </Typography>
      <ScheduleFields data={data} onChange={onChange} />

      {(recurringState !== 'hidden' || data.isRecurring) && (
        <SidebarSwitch
          checked={data.isRecurring}
          onChange={isRecurring => onChange({ isRecurring })}
          disabled={recurringState !== 'enabled'}
          label="Ceci est un récurrent"
        />
      )}
      {isCashdesk ? (
        <>
          {/* BeOne : da.cashdesk.preEdit < 2 ; une campagne déjà pré-éditée garde son switch visible */}
          {(can(visible(RIGHTS.cashdesk.preEdit)) || data.isPreEdited) && (
            <SidebarSwitch
              checked={data.isPreEdited}
              onChange={isPreEdited => onChange({ isPreEdited })}
              label="Offre pré-éditée"
            />
          )}
          <SidebarSwitch
            checked={data.noCreation}
            onChange={noCreation => onChange({ noCreation })}
            label="Ce coupon caisse n'a pas de création"
          />
        </>
      ) : (
        <SidebarSwitch
          checked={data.sendByCustomer}
          onChange={sendByCustomer => onChange({ sendByCustomer })}
          label={t('pages.marketing.communication.sendByCustomer')}
        />
      )}
      <SidebarSwitch
        checked={!data.hasOffer}
        onChange={noOffer => onChange({ hasOffer: !noOffer })}
        label={t('pages.marketing.communication.noOffer')}
      />

      {!data.hasOffer && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2, maxWidth: 360, alignItems: 'flex-start' }}>
          <DeferredTextField
            label={t('pages.marketing.communication.followUpDateStart')}
            type="date"
            value={data.followUpDateStart}
            onCommit={followUpDateStart => onChange({ followUpDateStart })}
            error={Boolean(errors.followUpDateStart)}
            helperText={errors.followUpDateStart}
            disabled={data.isRecurring}
          />
          <DeferredTextField
            label={t('pages.marketing.communication.followUpDateEnd')}
            type="date"
            value={data.followUpDateEnd}
            onCommit={followUpDateEnd => onChange({ followUpDateEnd })}
            error={Boolean(errors.followUpDateEnd)}
            helperText={errors.followUpDateEnd}
            disabled={data.isRecurring}
          />
        </Box>
      )}

      {showTestOperation && (
        <SidebarSwitch
          checked={data.isTestOperation}
          onChange={isTestOperation => onChange({ isTestOperation })}
          label={t('pages.marketing.communication.testOperation')}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

export default memo(CampaignDefinitionStep);
